import { effect } from "@preact/signals-react";
import type { RoomDep } from "./room.types";
import type { Lobby } from "../lobby/lobby.types";
import type { ActiveOmniMate } from "../localMates/activeOmniMate/activeOmniMate.types";
import type { LocalMates } from "../localMates/localMates/localMates.types";
import type { Mode } from "../mode/mode/mode.types";
import type { AuthDep } from "../auth/auth.types";

export interface RoomInitDeps {
  roomState: RoomDep["state"];
  roomActions: RoomDep["actions"];
  roomConnection: RoomDep["connection"];
  lobby: Lobby;
  activeOmniMate: ActiveOmniMate;
  localMates: LocalMates;
  mode: Mode;
  auth: AuthDep;
  workerUrl: string;
}

export type RoomCoreInitDeps = Pick<RoomInitDeps, "roomState" | "roomActions" | "activeOmniMate" | "localMates" | "mode"> & {
  roomConnection?: Pick<RoomDep["connection"], "status">
};

/**
 * The subset of `setupRoomInitialization`'s effects that have nothing to do
 * with the browser (no `window`/URL/history access) — safe to run in a
 * headless Node client (see containers/headlessContainer.ts). Syncs local
 * mates into the room and, while the room is in Omni mode, auto-switches
 * this client's active Omni mate to whoever's turn it is.
 */
export function setupRoomCoreEffects(deps: RoomCoreInitDeps) {
  // Sync Local Mates to Room
  effect(() => {
    const mates = deps.localMates.localMates.value;
    // The first effect run can precede creation of the room proxy, in which case the
    // update has nowhere to go. Re-run when connection state changes so a newly joined
    // local or networked room receives the current roster even when the roster is stable.
    void deps.roomConnection?.status.value;
    deps.roomActions.updateLocalMates(mates);
  });

  // Any connected client may act as - and submit moves for - any Omni mate in
  // the room, not just the client that created them, so this lists every
  // Omni mate currently in the room (room-wide, via roomState.mates), not just
  // this client's own locally-created ones. Each client still tracks its own
  // activeLocalOmniMateIndex independently, so which mate it's acting as is a
  // per-client choice even though the list of candidates is shared.
  effect(() => {
    const omniMateIds = deps.roomState.hasOmniMates.value
      ? deps.roomState.mates.value.filter((m: any) => m.isOmniMate).map((m: any) => m.mateId)
      : undefined;
    deps.activeOmniMate.updateLocalOmniMateIds(omniMateIds);
  });

  // The Omni mate this client acts as decides its host perspective (see roomState's localHostMateId).
  effect(() => {
    deps.roomState.actingOmniMateId.value = deps.activeOmniMate.activeLocalOmniMateId.value;
  });

  // Omni mode: auto-switch this client's active Omni mate to current turn.
  effect(() => {
    if (!deps.roomState.hasOmniMates.value) return;

    const game = deps.roomState.activeGame.value as any;
    const currentPlayer = game?.ctx?.currentPlayer;
    const playerToMateIds = game?.playerToMateIds as string[][] | undefined;

    deps.activeOmniMate.maybeAutoSwitchLocalOmniMate({
      isOmniMode: true,
      enabled: deps.activeOmniMate.autoSwitchLocalOmniMate.value,
      currentPlayer: typeof currentPlayer === 'string' ? currentPlayer : currentPlayer !== undefined ? String(currentPlayer) : undefined,
      playerToMateIds,
    });
  });
}

export function setupRoomInitialization(deps: RoomInitDeps) {
  const { roomConnection } = deps;
  let pendingJoinRoomId: string | null = null

  const requestJoinRoom = (roomId: string) => {
    if (pendingJoinRoomId === roomId) return
    pendingJoinRoomId = roomId
    deps.lobby.roomMode.value = 'room'
    void roomConnection.joinRoom(roomId)
  }

  // 1. URL Watcher: Sync URL with room connection status
  effect(() => {
    if (typeof window === "undefined") return;

    const currentPath = window.location.pathname;
    const roomIdMatch = currentPath.match(/^\/r\/([^\/]+)/);
    const roomIdInUrl = roomIdMatch ? roomIdMatch[1] : null;

    // If we are connected to a room, ensure URL reflects it
    if (roomConnection.status.value === "connected" && roomConnection.roomId.value) {
      if (roomIdInUrl !== roomConnection.roomId.value) {
        window.history.pushState(null, "", `/r/${roomConnection.roomId.value}`);
      }
    }
  });

  // 2. Auto-join room from URL
  effect(() => {
    if (typeof window === "undefined") return;
    
    // Only run once on mount/init effectively
    // Guard with error check to avoid infinite re-join loops on connection failure
    if (roomConnection.status.value !== "disconnected" || roomConnection.error.value) return;

    const currentPath = window.location.pathname;
    const roomIdMatch = currentPath.match(/^\/r\/([^\/]+)/);
    const roomIdInUrl = roomIdMatch ? roomIdMatch[1] : null;

    if (!roomIdInUrl) return;
    if (!deps.auth.isLoaded.value) return;

    const hasKnownName = !!deps.localMates.localMates.value[0]?.name;
    if (!hasKnownName && !deps.mode.isOmniMode() && !deps.mode.computedMode.value.sharedScreen) {
      deps.lobby.pendingRoomEntryAction.value = () => {
        requestJoinRoom(roomIdInUrl)
      }
      deps.lobby.roomMode.value = 'name-entry'
      return;
    }

    deps.lobby.pendingRoomEntryAction.value = null
    requestJoinRoom(roomIdInUrl)
  });
  // 3. Room Mode Transition: name-entry -> continue pending action
  effect(() => {
    if (typeof window === "undefined") return;

    const mates = deps.localMates.localMates.value;
    const hasName = mates.length > 0 && !!mates[0].name;

    if (deps.lobby.roomMode.value !== 'name-entry' || !hasName) {
      return
    }

    const pendingAction = deps.lobby.pendingRoomEntryAction.value
    deps.lobby.pendingRoomEntryAction.value = null

    if (!pendingAction) {
      deps.lobby.roomMode.value = 'room-selection';
      return
    }

    void pendingAction()
  });

  // 4. Room Mode Transition: room-selection -> room
  effect(() => {
    if (typeof window === "undefined") return;

    if (roomConnection.status.value === "connected" && roomConnection.roomId.value) {
      if (deps.lobby.roomMode.value === 'room-selection' || deps.lobby.roomMode.value === 'room-creation') {
        deps.lobby.roomMode.value = 'room';
      }
    }
  });

  // 5. Room Mode Transition: room -> room-selection (when leaving)
  effect(() => {
    if (typeof window === "undefined") return;
    const currentPath = window.location.pathname;
    const roomIdMatch = currentPath.match(/^\/r\/([^\/]+)/);
    const roomIdInUrl = roomIdMatch ? roomIdMatch[1] : null;

    if (roomConnection.status.value === "disconnected" && !roomConnection.roomId.value) {
      if (roomIdInUrl) {
        return;
      }
      if (deps.lobby.roomMode.value === "room" && !roomConnection.error.value) {
        deps.lobby.roomMode.value = "room-selection";
      }
    }
  });

  // 6-7. Non-browser effects (sync local mates to room, test-mode
  // auto-switch) — shared with the headless container.
  setupRoomCoreEffects(deps);
}
